import { type KeyboardEvent, useCallback, useRef } from 'react';

export function useTabsKeyboardNavigation<T>(
  items: T[] = [],
  setSelectedItem?: (item: T) => void
) {
  const tabsRef = useRef<Array<HTMLElement | null>>([]);

  const focusTabByIndex = useCallback(
    (index: number) => {
      const safeIndex = Math.max(0, Math.min(index, items.length - 1));
      tabsRef.current[safeIndex]?.focus();
    },
    [items.length]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent, index: number, data: T) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setSelectedItem?.(data);
        return;
      }

      let nextIndex = 0;

      switch (e.key) {
        case 'ArrowRight':
          nextIndex = (index + 1) % items.length;
          break;
        case 'ArrowLeft':
          nextIndex = (index - 1 + items.length) % items.length;
          break;
        default:
          return;
      }

      e.preventDefault();
      focusTabByIndex(nextIndex);
    },
    [items.length, focusTabByIndex, setSelectedItem]
  );

  return { tabsRef, handleKeyDown };
}
